import { Form, Select } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo } from "react"

import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"

interface ProjectViewFinanceNewRowMilestonesProps {
  form: FormInstance<FinancialTransactionItem>
  options: Record<string, BaseOptionType[]>
  projectTypeMilestonesMap: Map<string, BaseOptionType[]>
  currentMilestoneId?: string
  currentMilestoneName?: string
}

export function ProjectViewFinanceNewRowMilestones({
  projectTypeMilestonesMap,
  form,
  currentMilestoneId,
  currentMilestoneName
}: ProjectViewFinanceNewRowMilestonesProps) {
  const projectTypeID = Form.useWatch("project_type", form)
  const selectedMilestoneID = Form.useWatch("project_phase", form)

  const milestones = useMemo(
    () =>
      projectTypeID ? projectTypeMilestonesMap.get(projectTypeID) || [] : [],
    [projectTypeID, projectTypeMilestonesMap]
  )

  // The options are scoped to the selected project type, so a milestone with no
  // project type (or one outside that scope) is absent and the Select would fall
  // back to rendering the raw id. Keep the saved value's label resolvable.
  const milestoneOptions = useMemo(() => {
    const isUnchangedSelection =
      !!selectedMilestoneID && selectedMilestoneID === currentMilestoneId
    const isMissingFromOptions = !milestones.some(
      (option) => option.value === selectedMilestoneID
    )

    if (isUnchangedSelection && currentMilestoneName && isMissingFromOptions) {
      return [
        { value: currentMilestoneId, label: currentMilestoneName },
        ...milestones
      ]
    }

    return milestones
  }, [
    milestones,
    selectedMilestoneID,
    currentMilestoneId,
    currentMilestoneName
  ])

  return (
    <Form.Item<FinancialTransactionItem>
      name="project_phase"
      noStyle
      normalize={(value: string) => value || null}
    >
      <Select
        className="w-full"
        placeholder="Select milestone"
        options={milestoneOptions}
        showSearch
        allowClear
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
        }
      />
    </Form.Item>
  )
}
